import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { RoutingService } from '@services';
import { BehaviorSubject, fromEvent, Observable, of, Subject } from 'rxjs';
import { map, mergeMap, startWith } from 'rxjs/operators';

import {
  DiagnosticsOverlayTypeEnum,
  DiagnosticsUrlTypeEnum,
  FeaturesDetailsOverlayTypeEnum,
  PartnerAccessOverlayTypeEnum,
  RemoteServiceOverlayTypeEnum,
  RemoteServiceUrlTypeEnum,
  SingleRoomControlOverlayTypeEnum,
  SingleRoomControlUrlTypeEnum,
} from '../../+installations/installation/installation-overlay.model';
import { InstallationService } from '../../+installations/services/installation.service';
import { ViguideBreakpoints } from '../common/ui-constants';

@Injectable()
export class UIService {
  installationsQueryParams: {
    page?: number;
    size?: number;
    sortBy?: string;
    orderBy?: string;
    filter?: string;
    search?: string;
  } = {};

  installersQueryParams: {
    page?: number;
    size?: number;
  } = {};

  activePanel$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  activeOverlay$: BehaviorSubject<FeaturesDetailsOverlayTypeEnum> = new BehaviorSubject<
    FeaturesDetailsOverlayTypeEnum
  >(null);
  activeDiagnosticOverlay$: BehaviorSubject<DiagnosticsOverlayTypeEnum> = new BehaviorSubject<
    DiagnosticsOverlayTypeEnum
  >(null);
  activeRemoteServiceOverlay$: BehaviorSubject<RemoteServiceOverlayTypeEnum> = new BehaviorSubject<
    RemoteServiceOverlayTypeEnum
  >(null);
  activeSingleRoomControlOverlay$: BehaviorSubject<
    SingleRoomControlOverlayTypeEnum
  > = new BehaviorSubject<SingleRoomControlOverlayTypeEnum>(null);
  activePartnerAccessOverlay$: BehaviorSubject<PartnerAccessOverlayTypeEnum> = new BehaviorSubject<
    PartnerAccessOverlayTypeEnum
  >(null);
  isOverlayOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isDiagnosticOverlayOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isRemoteServiceOverlayOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isSingleRoomControlOverlayOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isActivePartnerAccessSettingsOverlayOpen$: BehaviorSubject<boolean> = new BehaviorSubject<
    boolean
  >(false);
  idOfOpenedWallboxDevice$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  closeExpandedMobileStickyPanel$: Subject<void> = new Subject();
  isSideNavOpenSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isAnyParameterRunning$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  currentWindowWidth$: Observable<number> = fromEvent(window, 'resize').pipe(
    startWith(window.innerWidth),
    map(() => window.innerWidth)
  );
  isMobileView$: Observable<boolean> = fromEvent(window, 'resize').pipe(
    startWith(''),
    map(() => this.isMobileView())
  );
  isTabletView$: Observable<boolean> = fromEvent(window, 'resize').pipe(
    startWith(''),
    map(() => this.isTabletView())
  );
  isAboveMediumWidth$: Observable<boolean> = fromEvent(window, 'resize').pipe(
    startWith(''),
    map(() => this.isAboveMediumWidth())
  );
  activeOverlayHeader$: Observable<any> = this.activeOverlay$.pipe(
    mergeMap((activeOverlay) => {
      switch (activeOverlay) {
        // feature tile details overlays
        case FeaturesDetailsOverlayTypeEnum.System:
        case FeaturesDetailsOverlayTypeEnum.WallboxSystem:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.SYSTEM.TITLE'));
        case FeaturesDetailsOverlayTypeEnum.WallboxChargingSettings:
          return of(
            this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.WALLBOX_CHARGING_SETTINGS.TITLE'
            )
          );
        case FeaturesDetailsOverlayTypeEnum.PvImmersionHeaterDetails:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.PV_HEATER.HEADER'));
        case FeaturesDetailsOverlayTypeEnum.Gateway:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.GATEWAY.TITLE'));
        case FeaturesDetailsOverlayTypeEnum.HeatingCircuit:
          return this.installationService.getCircuitName(
            this.installationService.selectedCircuitId$.getValue()
          );
        case FeaturesDetailsOverlayTypeEnum.CallForHeatHeatingZone:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.CALL_FOR_HEAT.TITLE')
          );
        case FeaturesDetailsOverlayTypeEnum.CircuitHeatingZone:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.HEATING_ZONE.TITLE')
          );

        case FeaturesDetailsOverlayTypeEnum.Dhw:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.DHW.TITLE'));
        case FeaturesDetailsOverlayTypeEnum.GasConsumption:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.GAS_CONSUMPTION'));
        case FeaturesDetailsOverlayTypeEnum.HeatProduction:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.HEAT_PRODUCTION'));
        case FeaturesDetailsOverlayTypeEnum.PowerProduction:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.POWER_PRODUCTION'));
        case FeaturesDetailsOverlayTypeEnum.SolarPowerProduction:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.SOLAR_POWER_PRODUCTION')
          );
        case FeaturesDetailsOverlayTypeEnum.ElectricityConsumption:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.POWER_CONSUMPTION'));
        case FeaturesDetailsOverlayTypeEnum.FuelConsumption:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.FUEL_CONSUMPTION'));
        case FeaturesDetailsOverlayTypeEnum.Autarky:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.AUTARKY'));
        case FeaturesDetailsOverlayTypeEnum.FuelCell:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.FUEL_CELL.TITLE'));
        case FeaturesDetailsOverlayTypeEnum.Ventilation:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.PANEL.VENTILATION'));
        case FeaturesDetailsOverlayTypeEnum.HemsSettings:
          return of(this.translateService.instant('+HEMS.SETTINGS.TITLE'));
        case FeaturesDetailsOverlayTypeEnum.ElectricHeater:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.PANEL.ELECTRIC_HEATER')
          );
        case FeaturesDetailsOverlayTypeEnum.SummaryGasConsumption:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.SUMMARY_GAS_CONSUMPTION')
          );
        case FeaturesDetailsOverlayTypeEnum.SummaryElectricityConsumption:
          return of(
            this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.SUMMARY_ELECTRICITY_CONSUMPTION'
            )
          );
        case FeaturesDetailsOverlayTypeEnum.Solar:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.SOLAR.TITLE'));
        case FeaturesDetailsOverlayTypeEnum.RefrigerantCircuit:
          return of(
            this.installationService.getRefrigerantCircuitName(
              this.installationService.selectedRefrigerantCircuitId$.getValue()
            )
          );
        case FeaturesDetailsOverlayTypeEnum.Battery:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.BATTERY.TITLE'));
        case FeaturesDetailsOverlayTypeEnum.Photovoltaics:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.PHOTOVOLTAICS.TITLE')
          );
        case FeaturesDetailsOverlayTypeEnum.Ventilation:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.PANEL.VENTILATION'));
        case FeaturesDetailsOverlayTypeEnum.MaintenanceChecklist:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.MAINTENANCE_CHECKLIST.TITLE')
          );
        case FeaturesDetailsOverlayTypeEnum.MaintenanceInformation:
          return of(
            this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.MAINTENANCE_INFORMATION.TITLE'
            )
          );
        case FeaturesDetailsOverlayTypeEnum.PowerLimitationHistoryLog:
          return of(
            this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.POWER_LIMITATION_HISTORY_LOG.TITLE'
            )
          );
        // menu bar overlays
        case FeaturesDetailsOverlayTypeEnum.ControlUnits:
          return of(
            this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.MENU_BAR.CONTROL_UNITS.TITLE'
            )
          );
        case FeaturesDetailsOverlayTypeEnum.EventLog:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.EVENT_LOG.BUTTON'));
        case FeaturesDetailsOverlayTypeEnum.Trending:
          return of(
            this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.MENU_BAR.DIAGNOSIS.DATA_TRENDING.TITLE'
            )
          );
        case FeaturesDetailsOverlayTypeEnum.CascadeLagDevice:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.CASCADES.HEADER'));
        case FeaturesDetailsOverlayTypeEnum.Ecowater:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.PANEL.ECOWATER'));
        case FeaturesDetailsOverlayTypeEnum.RemoteController:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.REMOTE_CONTROLLER.TITLE')
          );
        case FeaturesDetailsOverlayTypeEnum.ViCarePlans:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.VICARE_PLANS.TITLE')
          );
        case FeaturesDetailsOverlayTypeEnum.ViCareThermostat:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.VICARE_THERMOSTAT.TITLE')
          );
        case FeaturesDetailsOverlayTypeEnum.ServiceInstruction:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.SERVICE_INSTRUCTION.TITLE')
          );
        case FeaturesDetailsOverlayTypeEnum.SolarLog:
          return of(this.translateService.instant('+INSTALLATIONS.INSTALLATION.SOLARLOG.TITLE'));
        case FeaturesDetailsOverlayTypeEnum.InternalHydraulic:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.PANEL.INTERNAL_HYDRAULIC')
          );
        case FeaturesDetailsOverlayTypeEnum.ServiceLayer:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.PANEL.SERVICE_LAYER.TITLE')
          );
        case FeaturesDetailsOverlayTypeEnum.BufferTank:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.PANEL.BUFFER_TANK.TITLE')
          );
        case FeaturesDetailsOverlayTypeEnum.HybridProControl:
          return of(
            this.translateService.instant('+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.TITLE')
          );
        case FeaturesDetailsOverlayTypeEnum.DryContact:
          return of(
            this.translateService.instant('+INSTALLATIONS.DRY_CONTACTS.HEAT_PUMP.HEADER_DETAILS')
          );
        case FeaturesDetailsOverlayTypeEnum.DryContactGenericConsumer:
          return of(
            this.translateService.instant(
              '+INSTALLATIONS.DRY_CONTACTS.GENERIC_CONSUMER.HEAT_PUMP.HEADER_DETAILS'
            )
          );
        case FeaturesDetailsOverlayTypeEnum.PvPowerPrioritization:
          return of(
            this.translateService.instant(
              '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.TITLE'
            )
          );
        case FeaturesDetailsOverlayTypeEnum.ReferenceRoomSettings:
          return of(
            this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.REFERENCE_ROOM.OVERLAY_HEADER'
            )
          );
        default:
          return of('');
      }
    })
  );

  constructor(
    private translateService: TranslateService,
    private installationService: InstallationService,
    private routingService: RoutingService,
    private router: Router
  ) {}

  resetInstallationsQueryParams(): void {
    this.installationsQueryParams = {};
  }

  resetInstallersQueryParams(): void {
    this.installersQueryParams = {};
  }

  blockUI(): void {
    const body = document.querySelector('body');
    body.classList.add('block-ui');
  }

  unblockUI(): void {
    const body = document.querySelector('body');
    body.classList.remove('block-ui');
  }

  blockModalUI(): void {
    const modal = document.querySelector('.cdk-dialog-container');
    if (modal) {
      modal.classList.add('block-modal-ui');
    }
  }

  unblockModalUI(): void {
    const modal = document.querySelector('.cdk-dialog-container');
    if (modal) {
      modal.classList.remove('block-modal-ui');
    }
  }

  openActiveOverlay(overlayType: FeaturesDetailsOverlayTypeEnum): void {
    const body = document.querySelector('body');
    body.classList.add('body-scrollblock');

    this.activeOverlay$.next(overlayType);
    this.isOverlayOpen$.next(true);
  }

  openPartnerAccessOverlay(overlayType: PartnerAccessOverlayTypeEnum): void {
    const body = document.querySelector('body');
    body.classList.add('body-scrollblock');

    this.activePartnerAccessOverlay$.next(overlayType);
    this.isActivePartnerAccessSettingsOverlayOpen$.next(true);

    const currentUrl = this.router.url;
    const isUrlActivated = Object.values(PartnerAccessOverlayTypeEnum).some((path) =>
      currentUrl.includes('/' + path)
    );
    if (isUrlActivated) {
      return;
    }

    // Update route
    switch (overlayType) {
      case PartnerAccessOverlayTypeEnum.Settings:
        this.routingService.addChildPathToUrl(PartnerAccessOverlayTypeEnum.Settings);
        break;
    }
  }

  openSingleRoomControlOverlay(
    overlayType: SingleRoomControlOverlayTypeEnum,
    installationId?: number,
    gatewaySerial?: string
  ): void {
    const body = document.querySelector('body');
    body.classList.add('body-scrollblock');

    this.activeSingleRoomControlOverlay$.next(overlayType);
    this.isSingleRoomControlOverlayOpen$.next(true);

    if (installationId && gatewaySerial) {
      const currentUrl = this.router.url;
      const shouldReplaceChildPath = Object.values(SingleRoomControlUrlTypeEnum).some((value) =>
        currentUrl.includes(value)
      );

      switch (overlayType) {
        case SingleRoomControlOverlayTypeEnum.SingleRoomControlCommissioning:
          if (shouldReplaceChildPath) {
            this.routingService.replaceChildPathOnUrl(SingleRoomControlUrlTypeEnum.Commissioning);
          } else {
            this.routingService.addChildPathToUrl(SingleRoomControlUrlTypeEnum.Commissioning);
          }
          break;
        case SingleRoomControlOverlayTypeEnum.SingleRoomControlDetails:
          if (shouldReplaceChildPath) {
            this.routingService.replaceChildPathOnUrl(
              SingleRoomControlUrlTypeEnum.SingleRoomControl
            );
          } else {
            this.routingService.addChildPathToUrl(SingleRoomControlUrlTypeEnum.SingleRoomControl);
          }
          break;
      }
    }
  }

  openDiagnosticOverlay(
    overlayType: DiagnosticsOverlayTypeEnum,
    installationId?: number,
    gatewaySerial?: string,
    deviceSerial?: string
  ): void {
    const body = document.querySelector('body');
    body.classList.add('body-scrollblock');

    this.activeDiagnosticOverlay$.next(overlayType);
    this.isDiagnosticOverlayOpen$.next(true);
    const routingValues = [
      ...Object.values(DiagnosticsUrlTypeEnum),
      ...Object.values(SingleRoomControlUrlTypeEnum),
      ...Object.values(RemoteServiceUrlTypeEnum),
    ];

    if (installationId && gatewaySerial) {
      const currentUrl = this.router.url;
      if (routingValues.some((value) => currentUrl.includes(value))) {
        this.routingService.replaceChildPathOnUrl(DiagnosticsUrlTypeEnum[overlayType], {
          deviceSerial,
        });
      } else {
        this.routingService.addChildPathToUrl(DiagnosticsUrlTypeEnum[overlayType], {
          deviceSerial,
        });
      }
    }
  }

  openRemoteServiceOverlay(
    overlayType: RemoteServiceOverlayTypeEnum,
    installationId?: number,
    gatewaySerial?: string
  ): void {
    const isRemoteServiceOverlayOpen = this.isRemoteServiceOverlayOpen$.getValue();

    if (!isRemoteServiceOverlayOpen) {
      this.closeActiveOverlay(gatewaySerial, installationId);

      const body = document.querySelector('body');
      body.classList.add('body-scrollblock');
    }

    const routingValues = [
      ...Object.values(DiagnosticsUrlTypeEnum),
      ...Object.values(SingleRoomControlUrlTypeEnum),
      ...Object.values(RemoteServiceUrlTypeEnum),
    ];

    if (installationId && gatewaySerial) {
      const currentUrl = this.router.url;
      const changeOverlay =
        routingValues.some((value) => currentUrl.includes(value)) || isRemoteServiceOverlayOpen;

      if (changeOverlay) {
        this.routingService.replaceChildPathOnUrl(RemoteServiceUrlTypeEnum[overlayType], {});
      } else {
        this.routingService.addChildPathToUrl(RemoteServiceUrlTypeEnum[overlayType], {});
      }
    }

    this.activeRemoteServiceOverlay$.next(overlayType);
    this.isRemoteServiceOverlayOpen$.next(true);
  }

  closeActiveOverlay(
    gatewaySerial?: string,
    installationId?: number,
    removeDeviceSerialParam?: boolean
  ): void {
    const body = document.querySelector('body');
    body.classList.remove('body-scrollblock');

    this.activeOverlay$.next(FeaturesDetailsOverlayTypeEnum.None);
    this.activeDiagnosticOverlay$.next(DiagnosticsOverlayTypeEnum.None);
    this.activeRemoteServiceOverlay$.next(RemoteServiceOverlayTypeEnum.None);
    this.activeSingleRoomControlOverlay$.next(SingleRoomControlOverlayTypeEnum.None);
    this.activePartnerAccessOverlay$.next(PartnerAccessOverlayTypeEnum.None);
    this.isOverlayOpen$.next(false);
    this.isDiagnosticOverlayOpen$.next(false);
    this.isRemoteServiceOverlayOpen$.next(false);
    this.isSingleRoomControlOverlayOpen$.next(false);
    this.isActivePartnerAccessSettingsOverlayOpen$.next(false);

    if (gatewaySerial && installationId) {
      this.routingService.dropChildPathFromUrl(removeDeviceSerialParam, gatewaySerial);
    }
  }

  closeFeatureDetailsOverlayOnly(): void {
    this.activeOverlay$.next(FeaturesDetailsOverlayTypeEnum.None);
    this.isOverlayOpen$.next(false);
  }

  isSafariBrowser(): boolean {
    return (
      navigator.vendor &&
      navigator.vendor.indexOf('Apple') > -1 &&
      navigator.userAgent &&
      navigator.userAgent.indexOf('CriOS') === -1 &&
      navigator.userAgent.indexOf('FxiOS') === -1
    );
  }

  isMobileView(): boolean {
    return window.innerWidth < ViguideBreakpoints.sm;
  }
  isTabletView(): boolean {
    return window.innerWidth >= ViguideBreakpoints.sm && window.innerWidth < ViguideBreakpoints.md;
  }
  isAboveMediumWidth(): boolean {
    return window.innerWidth >= ViguideBreakpoints.md;
  }
}
